import { Alert } from '../types';

export const getModeDescription = (mode: Alert['mode']): string => {
  switch (mode) {
    case 'on_new_messages':
      return 'Inbox Messages Only';
    case 'when_no_messages':
      return 'When no messages are received';
    case 'always':
      return 'Always send alerts';
    default:
      return 'Unknown mode';
  }
};

export const getProcessMessagesDescription = (processMessages: Alert['processMessages']): string => {
  if (processMessages.length === 0) return 'None selected';
  
  // Check for the specific combination of 'not_assigned' and 'assigned_to_user'
  if (processMessages.length === 2 && 
      processMessages.includes('not_assigned') && 
      processMessages.includes('assigned_to_user')) {
    return 'Assigned to Me or Unassigned';
  }

  const descriptions = processMessages.map(msg => {
    switch (msg) {
      case 'assigned_to_user':
        return 'Assigned to Me';
      case 'not_assigned':
        return 'Unassigned messages';
      case 'all':
        return 'All messages';
      default:
        return 'Unknown filter';
    }
  });
  
  return descriptions.join(', ');
};

export const getPeriodDescription = (period: Alert['period']): string => {
  switch (period) {
    case 'minute':
      return ' minute';
    case 'hour':
      return ' hour';
    case 'day':
      return ' day';
    default:
      return 'Unknown period';
  }
};

export const getTriggerDescription = (onlyIfUnreadMessages?: boolean): string => {
  return onlyIfUnreadMessages ? 'If Unread Messages Exist' : 'Always';
};

export const getDaysOfWeekText = (daysOfWeek: number[]): string => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  if (daysOfWeek.length === 7) {
    return 'Every day';
  }
  
  if (daysOfWeek.length === 5 && daysOfWeek.every(day => day >= 1 && day <= 5)) {
    return 'Weekdays';
  }
  
  if (daysOfWeek.length === 2 && daysOfWeek.includes(0) && daysOfWeek.includes(6)) {
    return 'Weekends';
  }
  
  return [...daysOfWeek]
    .sort((a, b) => a - b)
    .map(day => dayNames[day])
    .join(', ');
};

export const sortAlerts = (alerts: Alert[], sortBy: string, sortOrder: string): Alert[] => {
  return [...alerts].sort((a, b) => {
    let aValue: string | number | boolean;
    let bValue: string | number | boolean;

    switch (sortBy) {
      case 'destinationEmail':
        aValue = (a.destinationEmail || '').toLowerCase();
        bValue = (b.destinationEmail || '').toLowerCase();
        break;
      case 'destinationPhoneNumber':
        aValue = a.destinationPhoneNumber || '';
        bValue = b.destinationPhoneNumber || '';
        break;
      case 'isActive':
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
        break;
      case 'mode':
        aValue = a.mode;
        bValue = b.mode;
        break;
      case 'period':
        aValue = a.period;
        bValue = b.period;
        break;
      default:
        return 0;
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });
};


export const validateAlert = (alert: Partial<Alert>): string[] => {
  const errors: string[] = [];

  if (!alert.destinationEmail && !alert.destinationPhoneNumber) {
    errors.push('Either email or phone number must be provided');
  }

  if (alert.interval !== undefined && alert.interval <= 0) {
    errors.push('Interval must be greater than 0');
  }

  if (alert.daysOfWeek && alert.daysOfWeek.some(day => day < 0 || day > 6)) {
    errors.push('Days of week must be between 0 (Sunday) and 6 (Saturday)');
  }

  return errors;
}; 